import { useState, useMemo, useEffect, useRef } from 'react';
import { format, addDays, addWeeks, startOfWeek, isToday } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const HOUR_HEIGHT = 48;
const START_HOUR = 5;
const END_HOUR = 24;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const statusStyle = {
  pending_admin: 'bg-orange-900/40 border-orange-500/60 text-orange-200',
  pending_doctor: 'bg-sky-900/40 border-sky-500/60 text-sky-200',
  pending: 'bg-amber-800/40 border-amber-500/60 text-amber-200',
  confirmed: 'cal-appointment-confirmed',
  cancelled: 'bg-stone-800/30 border-stone-600/60 text-stone-500 line-through opacity-50',
  no_show_patient: 'bg-violet-900/40 border-violet-500/60 text-violet-200 opacity-60',
  no_show_doctor: 'bg-blue-900/40 border-blue-500/60 text-blue-200 opacity-60',
};

const timeToMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const minToTime = (totalMin) => {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Convert Y pixel position to time string (snapped to 15-min)
const yToTime = (y) => {
  const rawMin = Math.round(((y / HOUR_HEIGHT) * 60) / 15) * 15;
  const absMin = START_HOUR * 60 + Math.max(0, rawMin);
  return minToTime(Math.min(absMin, END_HOUR * 60));
};

// Snap Y to 15-min grid pixel position
const snapY = (y) => {
  const rawMin = Math.round(((y / HOUR_HEIGHT) * 60) / 15) * 15;
  return (rawMin / 60) * HOUR_HEIGHT;
};

// 15-min grid approach: base schedule → then each override paints over in creation order
const calcWorkRanges = (dayKey, workingHours, dayOverrides, date = null, cyclicSchedule = null) => {
  const SLOT = 15;
  const working = new Set();

  // Check cyclic schedule first
  let cyclicApplied = false;
  if (cyclicSchedule?.enabled && cyclicSchedule?.start_date && date) {
    const cycleStart = new Date(cyclicSchedule.start_date + 'T00:00');
    const targetDate = new Date(date + 'T00:00');
    const daysDiff = Math.round((targetDate - cycleStart) / 86400000);
    cyclicApplied = true;
    let isWorkDay = false, startTime = '09:00', endTime = '18:00';

    if (cyclicSchedule.mode === 'pattern' && cyclicSchedule.pattern?.length) {
      const patLen = cyclicSchedule.pattern.length;
      const pos = ((daysDiff % patLen) + patLen) % patLen;
      const pd = cyclicSchedule.pattern[pos] || {};
      isWorkDay = pd.type === 'work';
      startTime = pd.start || '09:00';
      endTime = pd.end || '18:00';
    } else {
      const cycleLen = (cyclicSchedule.work_days || 2) + (cyclicSchedule.off_days || 2);
      const pos = ((daysDiff % cycleLen) + cycleLen) % cycleLen;
      isWorkDay = pos < (cyclicSchedule.work_days || 2);
      if (isWorkDay) {
        const dayIdx = Math.floor(pos);
        const dh = (cyclicSchedule.day_hours || [])[dayIdx];
        startTime = dh?.start || cyclicSchedule.start_time || '09:00';
        endTime = dh?.end || cyclicSchedule.end_time || '18:00';
      }
    }

    if (isWorkDay) {
      // Support multi-shift
      let shifts;
      if (cyclicSchedule.mode === 'pattern') {
        const patLen = cyclicSchedule.pattern.length;
        const pos2 = ((daysDiff % patLen) + patLen) % patLen;
        const pd2 = cyclicSchedule.pattern[pos2] || {};
        shifts = pd2.shifts || [{start: pd2.start || '09:00', end: pd2.end || '18:00'}];
      } else {
        const cycleLen2 = (cyclicSchedule.work_days || 2) + (cyclicSchedule.off_days || 2);
        const pos2 = ((daysDiff % cycleLen2) + cycleLen2) % cycleLen2;
        const dayIdx = Math.floor(pos2);
        const dh = (cyclicSchedule.day_hours || [])[dayIdx];
        shifts = dh?.shifts || [{start: dh?.start || cyclicSchedule.start_time || '09:00', end: dh?.end || cyclicSchedule.end_time || '18:00'}];
      }
      for (const shift of shifts) {
        for (let m = timeToMin(shift.start); m < timeToMin(shift.end); m += SLOT) working.add(m);
      }
    }
    // If off day, working stays empty
  }

  if (!cyclicApplied) {
    // 1. Base weekly schedule
    const base = workingHours?.[dayKey];
    if (base?.enabled) {
      for (let m = timeToMin(base.start); m < timeToMin(base.end); m += SLOT) working.add(m);
    }
  }

  // 2. Apply overrides in creation order
  const sorted = [...dayOverrides].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  for (const o of sorted) {
    if (o.override_type === 'remove' && !o.start) {
      working.clear();
    } else if (o.start && o.end) {
      const s = timeToMin(o.start), e = timeToMin(o.end);
      for (let m = s; m < e; m += SLOT) {
        if (o.override_type === 'add') working.add(m);
        else working.delete(m);
      }
    }
  }

  // 3. Convert to ranges
  const mins = [...working].sort((a, b) => a - b);
  const ranges = [];
  for (const m of mins) {
    if (ranges.length && m === ranges[ranges.length - 1][1]) {
      ranges[ranges.length - 1][1] = m + SLOT;
    } else {
      ranges.push([m, m + SLOT]);
    }
  }
  return ranges;
};

// Invert working ranges into non-working (dark) ranges within the visible grid
const calcDarkRanges = (workRanges) => {
  const gStart = START_HOUR * 60, gEnd = END_HOUR * 60;
  const dark = [];
  let cur = gStart;
  for (const [s, e] of workRanges) {
    const cs = Math.max(s, gStart), ce = Math.min(e, gEnd);
    if (cur < cs) dark.push([cur, cs]);
    cur = Math.max(cur, ce);
  }
  if (cur < gEnd) dark.push([cur, gEnd]);
  return dark;
};

export const CalendarView = ({ appointments = [], workingHours = {}, overrides = [], cyclicSchedule = null, onAppointmentClick, onCellClick, onReschedule, onRangeChange }) => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [drag, setDrag] = useState(null); // { id, offsetY, duration }
  const [dragPreview, setDragPreview] = useState(null); // { dateStr, time, y }

  // Selection state for range select
  const [sel, setSel] = useState(null); // { dateStr, startY, endY, isWorking }
  const selRef = useRef(null);
  const onCellClickRef = useRef(onCellClick);
  const longPressTimer = useRef(null);
  const touchStartPos = useRef(null);
  const touchSelecting = useRef(false);
  const touchDragTimer = useRef(null);
  const touchDragRef = useRef(null);
  const touchDragging = useRef(false);
  const suppressAppointmentClick = useRef(false);

  // Block scroll only after a long press has activated a selection or appointment drag.
  useEffect(() => {
    const preventScroll = (e) => {
      if (touchSelecting.current || touchDragging.current) e.preventDefault();
    };
    document.addEventListener('touchmove', preventScroll, { passive: false });
    return () => {
      document.removeEventListener('touchmove', preventScroll);
      clearTimeout(longPressTimer.current);
      clearTimeout(touchDragTimer.current);
    };
  }, []);

  useEffect(() => { selRef.current = sel; }, [sel]);
  useEffect(() => { onCellClickRef.current = onCellClick; }, [onCellClick]);

  // Global mouseup finalizes the selection
  useEffect(() => {
    const handleUp = () => {
      const s = selRef.current;
      if (!s) return;
      const topY = Math.min(s.startY, s.endY);
      const bottomY = Math.max(s.startY, s.endY);
      let startTime = yToTime(topY);
      let endTime = yToTime(bottomY);
      if (startTime === endTime) {
        const m = timeToMin(startTime);
        endTime = minToTime(Math.min(m + 60, END_HOUR * 60));
      }
      onCellClickRef.current?.({ date: s.dateStr, startTime, endTime, isWorking: s.isWorking });
      setSel(null);
    };
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchend', handleUp);
    return () => {
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchend', handleUp);
    };
  }, []);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const hasExternalBusy = useMemo(
    () => appointments.some((appointment) => appointment.is_external_busy),
    [appointments]
  );

  useEffect(() => {
    onRangeChange?.({
      dateFrom: format(weekStart, 'yyyy-MM-dd'),
      dateTo: format(addDays(weekStart, 6), 'yyyy-MM-dd'),
    });
  }, [weekStart, onRangeChange]);

  const getApptsForDay = (date) => {
    const ds = format(date, 'yyyy-MM-dd');
    return appointments.filter((a) => a.date === ds && a.status !== 'cancelled');
  };

  const blockStyle = (appt) => {
    const [h, m] = appt.time.split(':').map(Number);
    const gridStart = START_HOUR * 60;
    const gridEnd = END_HOUR * 60;
    const start = h * 60 + m;
    const end = start + (appt.duration || 60);
    const clippedStart = Math.max(start, gridStart);
    const clippedEnd = Math.min(end, gridEnd);
    if (clippedEnd <= clippedStart) return { display: 'none' };
    const top = ((clippedStart - gridStart) / 60) * HOUR_HEIGHT;
    const height = Math.max(((clippedEnd - clippedStart) / 60) * HOUR_HEIGHT - 2, 28);
    return { top: `${top}px`, height: `${height}px` };
  };

  // Overlap check for drag-and-drop
  const hasOverlap = (apptId, dateStr, time, duration) => {
    const tStart = timeToMin(time);
    const tEnd = tStart + duration;
    return appointments.some(a => {
      if (a.id === apptId || a.status === 'cancelled' || a.date !== dateStr) return false;
      const aStart = timeToMin(a.time);
      const aEnd = aStart + (a.duration || 60);
      return tStart < aEnd && tEnd > aStart;
    });
  };

  // Check if appointment fits entirely within working hours
  const fitsWorkingHours = (dateStr, time, duration, dayKey) => {
    const dayOvr = overrides.filter(o => o.date === dateStr);
    const workRanges = calcWorkRanges(dayKey, workingHours, dayOvr, dateStr, cyclicSchedule);
    const tStart = timeToMin(time);
    const tEnd = tStart + duration;
    return workRanges.some(([s, e]) => tStart >= s && tEnd <= e);
  };

  const touchPointToPreview = (clientX, clientY, offsetY) => {
    const target = document.elementFromPoint(clientX, clientY);
    const dayColumn = target?.closest?.('[data-calendar-day]');
    if (!dayColumn) return null;

    const rect = dayColumn.getBoundingClientRect();
    const y = Math.max(0, Math.min(clientY - rect.top - offsetY, totalHeight));
    return {
      dateStr: dayColumn.dataset.calendarDay,
      time: yToTime(y),
      y: snapY(y),
    };
  };

  const clearTouchDrag = () => {
    clearTimeout(touchDragTimer.current);
    touchDragTimer.current = null;
    touchDragRef.current = null;
    touchDragging.current = false;
    setDrag(null);
    setDragPreview(null);
  };

  const startAppointmentTouchDrag = (e, appt, isExternalBusy) => {
    if (isExternalBusy || e.touches.length !== 1) return;
    e.stopPropagation();

    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const gesture = {
      id: appt.id,
      duration: appt.duration || 60,
      offsetY: touch.clientY - rect.top,
      startX: touch.clientX,
      startY: touch.clientY,
      active: false,
      preview: null,
    };
    touchDragRef.current = gesture;
    suppressAppointmentClick.current = false;
    clearTimeout(touchDragTimer.current);
    touchDragTimer.current = setTimeout(() => {
      if (touchDragRef.current !== gesture) return;
      gesture.active = true;
      touchDragging.current = true;
      suppressAppointmentClick.current = true;
      const preview = touchPointToPreview(touch.clientX, touch.clientY, gesture.offsetY);
      gesture.preview = preview;
      setDrag({ id: gesture.id, offsetY: gesture.offsetY, duration: gesture.duration });
      setDragPreview(preview);
    }, 350);
  };

  const moveAppointmentTouchDrag = (e) => {
    const gesture = touchDragRef.current;
    const touch = e.touches[0];
    if (!gesture || !touch) return;

    if (!gesture.active) {
      const dx = Math.abs(touch.clientX - gesture.startX);
      const dy = Math.abs(touch.clientY - gesture.startY);
      if (dx > 10 || dy > 10) clearTouchDrag();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const preview = touchPointToPreview(touch.clientX, touch.clientY, gesture.offsetY);
    gesture.preview = preview;
    setDragPreview(preview);
  };

  const finishAppointmentTouchDrag = (e) => {
    const gesture = touchDragRef.current;
    clearTimeout(touchDragTimer.current);
    if (!gesture?.active) {
      touchDragRef.current = null;
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const touch = e.changedTouches[0];
    const preview = touch
      ? touchPointToPreview(touch.clientX, touch.clientY, gesture.offsetY) || gesture.preview
      : gesture.preview;
    const appt = appointments.find((item) => item.id === gesture.id);
    clearTouchDrag();
    window.setTimeout(() => { suppressAppointmentClick.current = false; }, 500);
    if (!preview || !appt) return;
    if (preview.dateStr === appt.date && preview.time === appt.time) return;

    const targetDay = new Date(`${preview.dateStr}T12:00:00`);
    const targetDayKey = DAY_KEYS[targetDay.getDay()];
    if (!fitsWorkingHours(preview.dateStr, preview.time, gesture.duration, targetDayKey)) {
      toast.error('Нельзя перенести на нерабочие часы');
      return;
    }
    if (hasOverlap(gesture.id, preview.dateStr, preview.time, gesture.duration)) {
      toast.error('Нельзя перенести: пересечение с другой записью');
      return;
    }
    onReschedule?.(gesture.id, preview.dateStr, preview.time);
  };

  const cancelAppointmentTouchDrag = (e) => {
    if (touchDragRef.current?.active) {
      e.preventDefault();
      e.stopPropagation();
    }
    clearTouchDrag();
    suppressAppointmentClick.current = false;
  };

  const now = new Date();
  const nowOffset = ((now.getHours() - START_HOUR) * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT;
  const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;

  return (
    <div data-testid="calendar-view" className="rounded-xl overflow-hidden animate-fade-in select-none" style={{ background: 'var(--app-card)', border: '1px solid var(--app-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid var(--app-border)' }}>
        <div className="flex items-center gap-1">
          <Button data-testid="cal-prev-week" variant="ghost" size="icon" onClick={() => setWeekStart((w) => addWeeks(w, -1))} className="h-8 w-8" style={{ color: 'var(--app-text-muted)' }}>
            <ChevronLeft size={18} />
          </Button>
          <Button data-testid="cal-today" variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
            Сегодня
          </Button>
          <Button data-testid="cal-next-week" variant="ghost" size="icon" onClick={() => setWeekStart((w) => addWeeks(w, 1))} className="h-8 w-8" style={{ color: 'var(--app-text-muted)' }}>
            <ChevronRight size={18} />
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <h3 className="font-heading text-sm capitalize" style={{ color: 'var(--app-text)' }}>
            {format(weekStart, 'LLLL yyyy', { locale: ru })}
          </h3>
        </div>
      </div>

      <div role="list" aria-label="Обозначения календаря"
        className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-5 py-2 text-[11px]"
        style={{ color: 'var(--app-text-muted)', borderBottom: '1px solid var(--app-border)' }}>
        <div role="listitem" className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="cal-work-swatch block h-3 w-3 rounded-[3px]" aria-hidden="true" />
          <span>Доступно для записи</span>
        </div>
        <div role="listitem" className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="cal-appointment-swatch block h-3 w-3 rounded-[3px]" aria-hidden="true" />
          <span>Запись ProPraxis</span>
        </div>
        {hasExternalBusy && (
          <div role="listitem" className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="cal-google-busy-swatch block h-3 w-3 rounded-[3px]" aria-hidden="true" />
            <span>Занято в Google</span>
          </div>
        )}
      </div>

      {/* Scrollable calendar */}
      <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
        <div className="min-w-[700px]">

          {/* Day headers (sticky) */}
          <div className="grid grid-cols-[40px_repeat(7,1fr)] lg:grid-cols-[56px_repeat(7,1fr)] sticky top-0 z-20" style={{ borderBottom: '1px solid var(--app-border)', background: 'var(--app-card-deep)' }}>
            <div />
            {days.map((day) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayKey = DAY_KEYS[day.getDay()];
              const dayOvr = overrides.filter((o) => o.date === dateStr);
              const workRanges = calcWorkRanges(dayKey, workingHours, dayOvr, dateStr, cyclicSchedule);
              const hasAnyWork = workRanges.length > 0;
              const hasOverrides = dayOvr.length > 0;
              const ovrTooltip = dayOvr.map(o =>
                o.override_type === 'remove'
                  ? `Выходной${o.note ? ': ' + o.note : ''}`
                  : `Доп. часы ${o.start}–${o.end}${o.note ? ' (' + o.note + ')' : ''}`
              ).join('\n');
              return (
                <div key={day.toISOString()} className={`py-2 text-center ${!hasAnyWork ? 'opacity-30' : ''}`} style={{ borderLeft: '1px solid var(--app-border)' }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--app-text-muted)' }}>{format(day, 'EEEEEE', { locale: ru })}</p>
                  <p className={`text-base font-semibold mt-0.5`} style={{ color: isToday(day) ? 'var(--app-accent)' : 'var(--app-text)' }}>
                    {format(day, 'd')}
                  </p>
                  {hasOverrides && (
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/60 mx-auto mt-0.5" title={ovrTooltip} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Grid body */}
          <div className="grid grid-cols-[40px_repeat(7,1fr)] lg:grid-cols-[56px_repeat(7,1fr)]" style={{ height: `${totalHeight}px`, position: 'relative' }}>
          {/* Hour labels */}
          <div className="relative">
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute w-full pr-2 text-right"
                style={{
                  top: `${(h - START_HOUR) * HOUR_HEIGHT}px`,
                  transform: h === START_HOUR ? 'none' : 'translateY(-50%)',
                }}
              >
                <span className="text-[10px] leading-none" style={{ color: 'var(--app-text-faint)' }}>{`${String(h).padStart(2, '0')}:00`}</span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayKey = DAY_KEYS[day.getDay()];
            const dayOvr = overrides.filter((o) => o.date === dateStr);

            // Compute effective working ranges and dark (non-working) ranges
            const workRanges = calcWorkRanges(dayKey, workingHours, dayOvr, dateStr, cyclicSchedule);
            const darkRanges = calcDarkRanges(workRanges);
            const hasAnyWork = workRanges.length > 0;
            const dayAppts = getApptsForDay(day);

            return (
              <div
                key={day.toISOString()}
                data-testid={`cal-day-${dateStr}`}
                data-calendar-day={dateStr}
                className="relative cursor-crosshair"
                style={{ borderLeft: '1px solid var(--app-border)', height: `${totalHeight}px` }}
                onMouseDown={(e) => {
                  if (e.target.closest('[data-appt]') || e.button !== 0) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top;
                  setSel({ dateStr, startY: y, endY: y, isWorking: hasAnyWork });
                }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget?.getBoundingClientRect();
                  if (!rect) return;
                  const clientY = e.clientY;
                  setSel(prev => {
                    if (!prev || prev.dateStr !== dateStr) return prev;
                    const y = Math.max(0, Math.min(clientY - rect.top, totalHeight));
                    return { ...prev, endY: y };
                  });
                }}
                onTouchStart={(e) => {
                  if (e.target.closest('[data-appt]')) return;
                  const touch = e.touches[0];
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = touch.clientY - rect.top;
                  touchStartPos.current = { x: touch.clientX, y: touch.clientY, colY: y, dateStr, isWorking: hasAnyWork };
                  touchSelecting.current = false;
                  longPressTimer.current = setTimeout(() => {
                    touchSelecting.current = true;
                    setSel({ dateStr, startY: y, endY: y, isWorking: hasAnyWork });
                  }, 400);
                }}
                onTouchMove={(e) => {
                  const touch = e.touches[0];
                  // If not yet in selection mode, check if finger moved too much → cancel long press (it's a scroll)
                  if (!touchSelecting.current) {
                    if (touchStartPos.current) {
                      const dx = Math.abs(touch.clientX - touchStartPos.current.x);
                      const dy = Math.abs(touch.clientY - touchStartPos.current.y);
                      if (dx > 10 || dy > 10) {
                        clearTimeout(longPressTimer.current);
                        touchStartPos.current = null;
                      }
                    }
                    return;
                  }
                  // In selection mode — update range and prevent scroll
                  e.preventDefault();
                  const rect = e.currentTarget?.getBoundingClientRect();
                  if (!rect) return;
                  setSel(prev => {
                    if (!prev || prev.dateStr !== dateStr) return prev;
                    const y = Math.max(0, Math.min(touch.clientY - rect.top, totalHeight));
                    return { ...prev, endY: y };
                  });
                }}
                onTouchEnd={() => {
                  clearTimeout(longPressTimer.current);
                  touchStartPos.current = null;
                  touchSelecting.current = false;
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (!drag) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top - drag.offsetY;
                  const time = yToTime(Math.max(y, 0));
                  setDragPreview({ dateStr, time, y: snapY(Math.max(y, 0)) });
                }}
                onDragLeave={() => setDragPreview(prev => prev?.dateStr === dateStr ? null : prev)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragPreview(null);
                  const apptId = e.dataTransfer.getData('apptId');
                  if (!apptId) return;
                  const offsetY = parseInt(e.dataTransfer.getData('offsetY') || '0', 10);
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top - offsetY;
                  const time = yToTime(Math.max(y, 0));
                  const appt = appointments.find(a => a.id === apptId);
                  const dur = appt?.duration || 60;
                  if (!fitsWorkingHours(dateStr, time, dur, dayKey)) {
                    toast.error('Нельзя перенести на нерабочие часы');
                    setDrag(null);
                    return;
                  }
                  if (appt && hasOverlap(apptId, dateStr, time, dur)) {
                    toast.error('Нельзя перенести: пересечение с другой записью');
                    setDrag(null);
                    return;
                  }
                  onReschedule?.(apptId, dateStr, time);
                  setDrag(null);
                }}
              >
                {/* Working hour highlights */}
                {workRanges.map(([s, e], i) => (
                  <div
                    key={`work-${i}`}
                    className="absolute left-0 right-0 pointer-events-none"
                    style={{
                      top: `${((s - START_HOUR * 60) / 60) * HOUR_HEIGHT}px`,
                      height: `${((e - s) / 60) * HOUR_HEIGHT}px`,
                      background: 'var(--cal-work-bg)',
                      boxShadow: 'inset 0 0 0 1px var(--cal-work-border)',
                    }}
                  />
                ))}

                {/* Non-working hour overlays */}
                {darkRanges.map(([s, e], i) => (
                  <div
                    key={`dark-${i}`}
                    className="absolute left-0 right-0 pointer-events-none"
                    style={{
                      top: `${((s - START_HOUR * 60) / 60) * HOUR_HEIGHT}px`,
                      height: `${((e - s) / 60) * HOUR_HEIGHT}px`,
                      background: 'var(--cal-off-bg)',
                    }}
                  />
                ))}

                {/* Time grid stays above schedule backgrounds so it remains visible in available hours. */}
                {HOURS.map((h) => (
                  <div
                    key={`hour-${h}`}
                    aria-hidden="true"
                    className="absolute w-full pointer-events-none"
                    style={{
                      top: `${(h - START_HOUR) * HOUR_HEIGHT}px`,
                      borderTop: '1px solid var(--app-border)',
                    }}
                  />
                ))}
                {HOURS.map((h) => (
                  <div
                    key={`half-${h}`}
                    aria-hidden="true"
                    className="absolute w-full border-t border-dashed pointer-events-none"
                    style={{
                      top: `${(h - START_HOUR) * HOUR_HEIGHT + HOUR_HEIGHT / 2}px`,
                      borderColor: 'var(--app-border-subtle)',
                    }}
                  />
                ))}

                {/* Now indicator */}
                {isToday(day) && nowOffset > 0 && nowOffset < totalHeight && (
                  <div className="absolute w-full z-20 pointer-events-none" style={{ top: `${nowOffset}px` }}>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-red-500 -ml-1 shrink-0" />
                      <div className="flex-1 h-[1.5px] bg-red-500/60" />
                    </div>
                  </div>
                )}

                {/* Selection overlay */}
                {sel && sel.dateStr === dateStr && (() => {
                  const top = snapY(Math.min(sel.startY, sel.endY));
                  const bottom = snapY(Math.max(sel.startY, sel.endY));
                  const h = Math.max(bottom - top, HOUR_HEIGHT / 4);
                  const t1 = yToTime(Math.min(sel.startY, sel.endY));
                  const t2 = yToTime(Math.max(sel.startY, sel.endY));
                  return (
                    <div
                      data-testid="cal-selection"
                      className="absolute left-0.5 right-0.5 bg-amber-500/15 border border-amber-500/30 rounded-md z-30 pointer-events-none"
                      style={{ top: `${top}px`, height: `${h}px` }}
                    >
                      <span className="absolute -top-5 left-1 text-[10px] text-amber-400 font-medium whitespace-nowrap">
                        {t1} — {t1 === t2 ? minToTime(Math.min(timeToMin(t1) + 60, END_HOUR * 60)) : t2}
                      </span>
                    </div>
                  );
                })()}

                {/* Drag preview indicator */}
                {drag && dragPreview && dragPreview.dateStr === dateStr && (
                  <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top: `${dragPreview.y}px` }}>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-amber-500 -ml-1 shrink-0" />
                      <div className="flex-1 h-[1.5px] bg-amber-500/80" />
                    </div>
                    <span className="absolute -top-5 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap" style={{ color: 'var(--app-accent)', background: 'var(--app-card)' }}>
                      {dragPreview.time}
                    </span>
                  </div>
                )}

                {/* Appointment blocks — draggable */}
                {dayAppts.map((appt) => {
                  const st = blockStyle(appt);
                  const isExternalBusy = Boolean(appt.is_external_busy);
                  const cls = statusStyle[appt.status] || statusStyle.pending;
                  const isDragging = drag?.id === appt.id;
                  return (
                    <div
                      key={appt.id}
                      data-appt="true"
                      data-testid={`cal-appt-${appt.id}`}
                      aria-label={isExternalBusy ? `Google Calendar: занято с ${appt.time}` : undefined}
                      title={isExternalBusy ? `Занято в Google Calendar с ${appt.time}` : undefined}
                      draggable={!isExternalBusy}
                      onDragStart={(e) => {
                        if (isExternalBusy) {
                          e.preventDefault();
                          return;
                        }
                        // Store grab offset so drop position is accurate
                        const offsetY = e.clientY - e.target.getBoundingClientRect().top;
                        e.dataTransfer.setData('apptId', appt.id);
                        e.dataTransfer.setData('offsetY', String(offsetY));
                        e.dataTransfer.effectAllowed = 'move';
                        setDrag({ id: appt.id, offsetY, duration: appt.duration || 60 });
                      }}
                      onDragEnd={() => { setDrag(null); setDragPreview(null); }}
                      onTouchStart={(e) => startAppointmentTouchDrag(e, appt, isExternalBusy)}
                      onTouchMove={moveAppointmentTouchDrag}
                      onTouchEnd={finishAppointmentTouchDrag}
                      onTouchCancel={cancelAppointmentTouchDrag}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (suppressAppointmentClick.current) {
                          e.preventDefault();
                          suppressAppointmentClick.current = false;
                          return;
                        }
                        if (isExternalBusy) {
                          toast.info('Это время занято в Google Calendar');
                          return;
                        }
                        onAppointmentClick?.(appt);
                      }}
                      className={isExternalBusy
                        ? 'cal-google-busy absolute left-1 right-1 rounded-md px-2 py-1 text-left cursor-not-allowed z-[5] overflow-hidden'
                        : `cal-propraxis-appointment absolute left-1 right-1 ${cls} ${appt.is_group ? 'border-violet-500' : ''} border rounded-md px-2 py-1 text-left cursor-grab active:cursor-grabbing hover:brightness-110 transition-[filter,opacity,transform] duration-150 z-10 overflow-hidden ${isDragging ? 'opacity-30 scale-95' : ''}`}
                      style={st}
                    >
                      <p className={`text-xs truncate ${isExternalBusy ? 'font-medium' : 'font-semibold'}`}>
                        {isExternalBusy
                          ? 'Google Calendar'
                          : appt.is_group
                          ? `${[appt.patient_name, ...(appt.additional_patients || []).map(p => p.name)].slice(0, 3).join(', ')}${(appt.additional_patients || []).length > 2 ? '...' : ''}`
                          : appt.patient_name}
                      </p>
                      <p className="text-[10px] truncate" style={{
                        color: isExternalBusy
                          ? 'var(--cal-google-busy-muted)'
                          : appt.status === 'confirmed'
                          ? 'var(--cal-appointment-muted)'
                          : 'var(--app-text-muted)',
                      }}>
                        {appt.time} &middot; {isExternalBusy ? 'занято' : appt.format_name}
                        {isExternalBusy && appt.doctor_name ? ` · ${appt.doctor_name}` : null}
                        {!isExternalBusy && appt.is_group && <span className="ml-1 text-violet-400">({1 + (appt.additional_patients || []).length})</span>}
                      </p>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </div>
  );
};
